/**
 * Card de regularización del Inicio (007 · T034 y T068; contracts/ui.md §1.2, research R-15,
 * spec US1-1/US1-3, US3-3 y US4-3).
 *
 * Una sola card, encima de "Tu jornada", con el **primer ítem accionable** de la respuesta
 * (`can_request`), que ya llega ordenado por el servidor: `ASKED` → huecos por regularizar y
 * rechazadas/canceladas con cupo (ancla más antigua primero). La UI no reordena ni decide nada.
 *
 * | Estado | Título | Cuerpo | Acción |
 * |---|---|---|---|
 * | `ASKED` | "Tu administrador te pide registrar la salida del martes 23" | la nota entre comillas (o el resumen del ancla) | "Regularizar" |
 * | `SUGGESTED` (y `CANCELLED` con cupo) | "Te falta registrar la salida del martes 23" | "Entrada 08:57 · Angamos" | "Regularizar" |
 * | `REJECTED` | "No aprobaron tu salida del martes 23" | el motivo del administrador | "Proponer otra hora" |
 *
 * Fondo `--fm-warning-soft`, icono `clock-alert` 20 px + texto, botón **secundario** compacto
 * (≥ 44 px): el coral sólido sigue reservado a "MARCAR…". Con más accionables, "y N más" →
 * `/asistencia#regularizaciones`. Sin red la acción queda deshabilitada ("Sin conexión").
 */
import { Link } from '@tanstack/react-router';
import { ClockAlert } from 'lucide-react';

import type { StaffRegularizationItem, StaffRegularizationsResponse } from '@fermin/contracts';

import { Button, buttonClasses } from '../../components/ui/button';
import { t } from '../../i18n';

import { actionableItems } from './api';
import { anchorSummary, missingNoun, shortDay } from './format';

function titleOf(item: StaffRegularizationItem): string {
  const vars = { missing: missingNoun(item.missing_type), day: shortDay(item.anchor.operative_day) };
  if (item.state === 'ASKED') {
    return t('regularization.card.asked', vars);
  }
  if (item.state === 'REJECTED') {
    return t('regularization.card.rejected', vars);
  }
  return t('regularization.card.suggested', vars);
}

function bodyOf(item: StaffRegularizationItem): string {
  if (item.state === 'ASKED' && item.request?.ask_note) {
    return t('regularization.card.quotedNote', { note: item.request.ask_note });
  }
  if (item.state === 'REJECTED' && item.request?.resolution_note) {
    return item.request.resolution_note;
  }
  return anchorSummary(item.anchor);
}

export interface RegularizationCardProps {
  data: StaffRegularizationsResponse;
  online: boolean;
}

/** Primer ítem accionable o `null` (entonces no se pinta nada). */
export function primaryRegularization(
  data: StaffRegularizationsResponse,
): StaffRegularizationItem | null {
  return actionableItems(data.items)[0] ?? null;
}

export function RegularizationCard({ data, online }: RegularizationCardProps) {
  const item = primaryRegularization(data);
  if (item === null) {
    return null;
  }
  const more = Math.max(0, data.counts.actionable - 1);
  const actionLabel =
    item.state === 'REJECTED' ? t('regularization.card.retry') : t('regularization.card.action');
  const actionClasses = buttonClasses({ variant: 'secondary', size: 'md', fullWidth: false });

  return (
    <section
      data-testid="home-regularization"
      data-state={item.state}
      data-missing={item.missing_type}
      aria-labelledby="home-regularization-title"
      className="flex flex-col gap-fm-3 rounded-fm-lg bg-fm-warning-soft p-fm-4"
    >
      <div className="flex items-start gap-fm-2">
        <ClockAlert aria-hidden="true" size={20} className="mt-0.5 shrink-0 text-fm-warning-text" />
        <h2
          id="home-regularization-title"
          className="font-display text-fm-base font-bold text-fm-text"
        >
          {titleOf(item)}
        </h2>
      </div>
      <p data-testid="home-regularization-body" className="text-fm-base text-fm-text">
        {bodyOf(item)}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-fm-3">
        {online ? (
          <Link
            to="/regularizar/$markId"
            params={{ markId: item.anchor.mark_id }}
            data-testid="home-regularization-action"
            className={actionClasses}
          >
            {actionLabel}
          </Link>
        ) : (
          <Button
            data-testid="home-regularization-action"
            variant="secondary"
            size="md"
            fullWidth={false}
            disabled
          >
            {t('regularization.form.offline')}
          </Button>
        )}
        {more > 0 ? (
          <Link
            to="/asistencia"
            hash="regularizaciones"
            data-testid="home-regularization-more"
            className="inline-flex min-h-11 items-center font-ui text-fm-base font-semibold text-fm-text underline underline-offset-4"
          >
            {t('regularization.card.more', { count: more })}
          </Link>
        ) : null}
      </div>
    </section>
  );
}
